import { debounce } from 'lodash'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import useFetch from 'use-http'
import { API_URL } from '../../constants/api'
import { SEARCH_DELAY } from '../../constants/static'
import useUploadStore from '../../store/uploadStore'
import { isValid } from '../../utils'
import Label from './Label'

interface Suggestion {
  id: string
  name: string
  type: string
  thumbnail: string
}
interface AutoSuggestionProps {
  currentValue: string
  setCurrentValue: (searchedValue: string) => void
  category: string
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
function useOutsideAlerter(ref: any) {
  const [clickedOut, setClickedOut] = useState(false)
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        setClickedOut(true)
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref])
  useEffect(() => {
    if (clickedOut) setClickedOut(false)
  }, [clickedOut])

  return clickedOut
}

const AutoSuggestionSearch = ({
  currentValue,
  setCurrentValue,
  category,
}: AutoSuggestionProps) => {
  const [currentList, setCurrentList] = useState<Suggestion[]>([])
  const [xtraSearch, setXtraSearch] = useState(false)
  const suggestionListRef = useRef(null)
  const router = useRouter()
  const clickedOut = useOutsideAlerter(suggestionListRef)
  const { categoryList } = useUploadStore()
  const { post, response, loading } = useFetch(API_URL)

  useEffect(() => {
    if (clickedOut) {
      setXtraSearch(false)
      setCurrentList([])
    }
  }, [clickedOut])

  const getCategoryName = (categoryKey: string) => {
    return (
      categoryList.find((cat: any) => cat.key === categoryKey)?.name ??
      categoryKey
    )
  }

  const generateList = useCallback(
    debounce(async (searchedValue: string) => {
      if (searchedValue.length <= 2) {
        return
      }

      const dataFetched = await post('/redis/searchAssetName', {
        name: searchedValue,
        type: category,
      })
      if (response.ok) {
        setCurrentList(dataFetched?.data ?? [])
      }

      setXtraSearch(true)
    }, SEARCH_DELAY),
    [category]
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setXtraSearch(false)
    let searchedValue = event.target.value
    setCurrentValue(searchedValue)
    setCurrentList([])
    generateList(searchedValue)
  }

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.preventDefault()
    if (event.key === 'Enter' && currentValue.length > 2 && category) {
      router.push(`/categories/${category}/search/${currentValue}`)
    }
  }

  return (
    <form
      autoComplete="off"
      className="h-full grow leading-10 md:relative md:w-[320px]"
    >
      <div id="wrapper" ref={suggestionListRef}>
        <div className="flex">
          <input
            className="h-[40px] w-full bg-white/10 py-1 pl-2 text-white placeholder-white/20 focus:outline-none"
            type="text"
            id="auto-suggestion-search"
            placeholder="Type here to search for an item"
            onChange={handleChange}
            onFocus={() => {
              generateList(currentValue)
            }}
            value={currentValue}
            onKeyDown={handleOnKeyDown}
          />
          <div className="flex w-[48px] items-center justify-center bg-white/10">
            <Image
              src="/icons/search_icon.svg"
              alt="search"
              width={24}
              height={24}
            />
          </div>
        </div>
        <div
          id="categories-container"
          className="absolute left-0 w-screen overflow-y-scroll rounded bg-black/90 text-white/50 md:max-h-[350px] md:w-full"
        >
          <ul className="rounded-lg">
            {loading && (
              <li className="cursor-pointer rounded pl-2" key={-2}>
                <div className="m-auto mt-2 mb-2 h-4 w-4 animate-spin rounded-full border-2 border-t-2 border-t-black"></div>
              </li>
            )}
            {currentList.length != 0 &&
              currentValue.length > 2 &&
              currentList.map((listItem: Suggestion) => (
                <Link
                  key={listItem.id}
                  href={
                    listItem.name.length > 2 && category
                      ? `/categories/${listItem.type}/${listItem.id}`
                      : '#'
                  }
                >
                  <li className="cursor-pointer overflow-auto whitespace-nowrap rounded py-2 pl-2 ease-in hover:bg-white/10">
                    <div className="flex">
                      <Image
                        src={
                          listItem?.thumbnail && isValid(listItem?.thumbnail)
                            ? listItem?.thumbnail
                            : '/images/placeholder.png'
                        }
                        blurDataURL="/images/placeholder.png"
                        className="object-cover"
                        width={60}
                        height={60}
                        placeholder="blur"
                        alt="thumbnail"
                      />
                      <div className="mt-1 ml-2 flex flex-col justify-center">
                        <Label text={getCategoryName(listItem.type)} />
                        <div className="text-sm md:text-base">
                          {listItem.name}
                        </div>
                      </div>
                    </div>
                  </li>
                </Link>
              ))}
            {!loading &&
              //   !currentList.length &&
              currentValue.length > 2 &&
              xtraSearch && (
                <Link
                  key={0}
                  href={
                    currentValue.length > 2 && category
                      ? `/categories/${category}/search/${currentValue}`
                      : '#'
                  }
                >
                  <li className="cursor-pointer overflow-auto whitespace-nowrap rounded pl-2 hover:bg-white/10">
                    {`Search ${currentValue}`}
                  </li>
                </Link>
              )}
          </ul>
        </div>
      </div>
    </form>
  )
}

export default memo(AutoSuggestionSearch)
